import * as ScrollAreaPrimitive from "@radix-ui/react-scroll-area";
import { cn } from "@reactive-resume/utils";
import { forwardRef, useCallback, useEffect, useRef, useState } from "react";

type ScrollAreaProps = React.ComponentPropsWithoutRef<typeof ScrollAreaPrimitive.Root> & {
  hideScrollbar?: boolean;
  orientation?: "vertical" | "horizontal";
  fadeEdges?: boolean;
  scrollHint?: boolean;
};

export const ScrollArea = forwardRef<
  React.ElementRef<typeof ScrollAreaPrimitive.Root>,
  ScrollAreaProps
>(
  (
    {
      type = "hover",
      orientation = "vertical",
      hideScrollbar = false,
      fadeEdges = false,
      scrollHint = false,
      className,
      children,
      ...props
    },
    ref,
  ) => {
    const viewportRef = useRef<HTMLDivElement>(null);
    const scrolledFromHintRef = useRef(false);
    const [edges, setEdges] = useState({ top: false, bottom: false });
    const [hasScrolled, setHasScrolled] = useState(false);

    const checkScroll = useCallback(() => {
      const el = viewportRef.current;
      if (!el) return;

      const hasOverflow = el.scrollHeight > el.clientHeight + 2;

      const newEdges = {
        top: el.scrollTop > 2,
        bottom: hasOverflow && el.scrollTop + el.clientHeight < el.scrollHeight - 2,
      };

      setEdges(newEdges);

      if (scrolledFromHintRef.current) {
        if (!newEdges.bottom) {
          setHasScrolled(true);
          scrolledFromHintRef.current = false;
        }
      } else {
        if (newEdges.top) setHasScrolled(true);
      }
      if (!hasOverflow) setHasScrolled(false);
    }, []);

    useEffect(() => {
      const el = viewportRef.current;
      if (!el) return;
      checkScroll();
      el.addEventListener("scroll", checkScroll, { passive: true });
      const ro = new ResizeObserver(checkScroll);
      ro.observe(el);
      return () => {
        el.removeEventListener("scroll", checkScroll);
        ro.disconnect();
      };
    }, [checkScroll]);

    const maskImage = fadeEdges
      ? [
          edges.top ? "linear-gradient(to bottom, transparent, black 24px)" : "none",
          edges.bottom ? "linear-gradient(to top, transparent, black 24px)" : "none",
        ]
          .filter((v) => v !== "none")
          .join(", ") || "none"
      : undefined;

    const showHint = scrollHint && !hasScrolled && edges.bottom;

    const scrollDownByHint = useCallback(() => {
      const el = viewportRef.current;
      if (!el) return;
      scrolledFromHintRef.current = true;
      const step = Math.max(200, Math.floor(el.clientHeight * 0.6));
      const next = Math.min(el.scrollTop + step, el.scrollHeight - el.clientHeight);
      el.scrollTo({ top: next, behavior: "smooth" });
      requestAnimationFrame(() => checkScroll());
    }, [checkScroll]);

    return (
      <ScrollAreaPrimitive.Root
        ref={ref}
        type={type}
        className={cn("relative overflow-hidden", className)}
        {...props}
      >
        <ScrollAreaPrimitive.Viewport
          ref={viewportRef}
          className={cn(
            "h-full w-full rounded-[inherit]",
            orientation === "vertical" && "[&>div]:!block [&>div]:min-h-0 [&>div]:min-w-0",
          )}
          style={maskImage && maskImage !== "none" ? { maskImage, WebkitMaskImage: maskImage } : undefined}
        >
          {children}
        </ScrollAreaPrimitive.Viewport>
        <ScrollBar orientation={orientation} className={cn(hideScrollbar && "opacity-0")} />
        <ScrollAreaPrimitive.Corner />

        {showHint && (
          <div
            role="button"
            tabIndex={0}
            aria-label="Scroll down"
            className="absolute inset-x-0 bottom-0 flex cursor-pointer justify-center pb-3 transition-opacity hover:opacity-90 active:opacity-80"
            onClick={scrollDownByHint}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                scrollDownByHint();
              }
            }}
          >
            <div className="animate-bounce rounded-full bg-primary p-1.5 text-primary-foreground shadow-lg">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="6 9 12 15 18 9" />
              </svg>
            </div>
          </div>
        )}
      </ScrollAreaPrimitive.Root>
    );
  },
);

ScrollArea.displayName = ScrollAreaPrimitive.Root.displayName;

export const ScrollBar = forwardRef<
  React.ElementRef<typeof ScrollAreaPrimitive.ScrollAreaScrollbar>,
  React.ComponentPropsWithoutRef<typeof ScrollAreaPrimitive.ScrollAreaScrollbar>
>(({ className, orientation, ...props }, ref) => (
  <ScrollAreaPrimitive.ScrollAreaScrollbar
    ref={ref}
    orientation={orientation}
    className={cn(
      "flex touch-none select-none transition-colors",
      orientation === "vertical" && "h-full w-2.5 border-l border-l-transparent p-[1px]",
      orientation === "horizontal" && "h-2.5 border-t border-t-transparent p-[1px]",
      className,
    )}
    {...props}
  >
    <ScrollAreaPrimitive.ScrollAreaThumb className="relative flex-1 rounded-full bg-border" />
  </ScrollAreaPrimitive.ScrollAreaScrollbar>
));

ScrollBar.displayName = ScrollAreaPrimitive.ScrollAreaScrollbar.displayName;
